/**
 * Cognito Hosted-UI domain registry (F-374 / US-261 — batch 4 S1).
 *
 * Mirrors the Stage → Cognito Domain registry in the landlord-pro
 * INFRASTRUCTURE.md so this SPA can never redirect OAuth traffic at an
 * inactive stage domain. Only ACTIVE domains belong here:
 *
 *   us2          → landlord-pro-prod-us-east-2      (pool us-east-2_OgJGh3gQx)
 *   us2-staging  → landlord-pro-staging-us-east-2   (pool us-east-2_LN8QlimNT)
 *   staging2     → landlord-pro-staging-eu-west-2   (pool eu-west-2_kY8bT7eNv)
 *   production   → eu-west-2lrknldok8               (pool eu-west-2_x0gLggsNO)
 *
 * (UK domains verified live 2026-10-19 against the pools' own Domain fields —
 * the `compliance-hub-staging`/`compliance-hub-prod` names the registry docs
 * carried were never provisioned and do not resolve in DNS.)
 *
 * Everything else — including the legacy `texas`/`texas-staging` Hosted-UI
 * domains — is intentionally absent: `resolveCognitoDomain` refuses to
 * return an unregistered value (NoSuchDomain). Domain + app-client
 * creation / callbackURLs are infra-owned (buffy B1 + cloud owner); this
 * file only *reads* the active registry.
 */

export interface StageCognito {
  stage: string;
  region: string;
  poolId: string;
  domain: string;
  callbackPath: string;
  logoutPath: string;
  market: 'texas' | 'uk';
}

/** Active stage → Cognito domain map (source of truth mirrors INFRASTRUCTURE.md). */
export const ACTIVE_COGNITO_STAGES: StageCognito[] = [
  {
    stage: 'us2',
    region: 'us-east-2',
    poolId: 'us-east-2_OgJGh3gQx',
    domain: 'landlord-pro-prod-us-east-2',
    callbackPath: '/auth/callback',
    logoutPath: '/logout',
    market: 'texas',
  },
  {
    stage: 'us2-staging',
    region: 'us-east-2',
    poolId: 'us-east-2_LN8QlimNT',
    domain: 'landlord-pro-staging-us-east-2',
    callbackPath: '/auth/callback',
    logoutPath: '/logout',
    market: 'texas',
  },
  {
    stage: 'staging2',
    region: 'eu-west-2',
    poolId: 'eu-west-2_kY8bT7eNv',
    domain: 'landlord-pro-staging-eu-west-2',
    callbackPath: '/auth/callback',
    logoutPath: '/logout',
    market: 'uk',
  },
  {
    stage: 'production',
    region: 'eu-west-2',
    poolId: 'eu-west-2_x0gLggsNO',
    domain: 'eu-west-2lrknldok8',
    callbackPath: '/auth/callback',
    logoutPath: '/logout',
    market: 'uk',
  },
];

/** Every registered (active) Cognito domain. Used for support-org lookups. */
export const REGISTERED_COGNITO_DOMAINS: string[] = ACTIVE_COGNITO_STAGES.map((s) => s.domain);

/** Every registered (active) user pool id. */
export const REGISTERED_COGNITO_POOLS: string[] = ACTIVE_COGNITO_STAGES.map((s) => s.poolId);

export class NoSuchDomainError extends Error {
  constructor(domain: string) {
    super(
      `NoSuchDomain: "${domain}" is not an active Cognito Hosted-UI domain (see INFRASTRUCTURE.md registry). ` +
        `Valid domains: ${REGISTERED_COGNITO_DOMAINS.join(', ') || '(none registered)'}.`
    );
    this.name = 'NoSuchDomainError';
  }
}

export function isRegisteredCognitoDomain(domain?: string): boolean {
  if (!domain) return false;
  return REGISTERED_COGNITO_DOMAINS.includes(domain);
}

export function isRegisteredCognitoPool(poolId?: string): boolean {
  if (!poolId) return false;
  return REGISTERED_COGNITO_POOLS.includes(poolId);
}

/**
 * Assert a domain is in the active registry. Throws NoSuchDomainError when it
 * is not — used by the S1 regression test to prove `texas` / `texas-staging`
 * and any other inactive domain are refused.
 */
export function assertRegisteredCognitoDomain(domain?: string): string {
  if (!domain || !isRegisteredCognitoDomain(domain)) {
    throw new NoSuchDomainError(domain || '<empty>');
  }
  return domain;
}

/** First active stage's domain — the prod default when the stage is ambiguous. */
export function defaultCognitoDomain(): string {
  if (ACTIVE_COGNITO_STAGES.length === 0) {
    throw new NoSuchDomainError('<no active stages>');
  }
  return ACTIVE_COGNITO_STAGES[0].domain;
}

/**
 * Resolve the Cognito Hosted-UI domain for this build.
 *
 * An explicitly provided `REACT_APP_COGNITO_DOMAIN` is honoured ONLY when it
 * is an active registered domain. Any other value (including the legacy
 * texas / texas-staging domains) is refused: the function warns and falls
 * back to the active default rather than redirecting OAuth traffic at an
 * inactive domain. The throw is available via assertRegisteredCognitoDomain.
 */
export function resolveCognitoDomain(provided?: string): string {
  if (provided && isRegisteredCognitoDomain(provided)) {
    return provided;
  }
  if (provided) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[Cognito] REACT_APP_COGNITO_DOMAIN="${provided}" is not an active registered domain; ` +
          `using "${defaultCognitoDomain()}" instead.`
      );
    }
    return defaultCognitoDomain();
  }
  return defaultCognitoDomain();
}

/**
 * Full Cognito Hosted-UI HOSTNAME for this build.
 *
 * The registry stores Cognito's domain *prefix* — the value typed into the
 * console (e.g. `landlord-pro-prod-us-east-2`). The OAuth endpoints only
 * exist on the full hostname `<prefix>.auth.<region>.amazoncognito.com`.
 *
 * Using the bare prefix as a hostname silently produces a name that does not
 * resolve: E-58 social sign-in 404'd at DNS on the /oauth2/authorize redirect
 * (the "Continue with Google" click produced a browser error page) and the
 * token exchange failed with "Failed to fetch" — verified live on the us2
 * lane, 2026-10-16. Every caller that builds a Hosted-UI URL must use this.
 */
export function hostedUiHost(provided?: string): string {
  const domain = resolveCognitoDomain(provided);
  const stage = ACTIVE_COGNITO_STAGES.find((s) => s.domain === domain);
  if (!stage) {
    throw new NoSuchDomainError(domain);
  }
  return `${stage.domain}.auth.${stage.region}.amazoncognito.com`;
}